import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CostTrackingProvider } from '../../contexts/CostTrackingContext';
import { PMProvider } from '../../contexts/PMContext';
import { useEquipmentContext } from '../../contexts/EquipmentContext';
import { ExpenseFilterBar } from './ExpenseFilterBar';
import { ExpenseTable } from './ExpenseTable';
import { CostSummaryFooter } from './CostSummaryFooter';
import { RecordExpenseForm } from './RecordExpenseForm';
import { ExpenseRecordDetail } from './ExpenseRecordDetail';
import { CostTrackingEquipmentList } from './CostTrackingEquipmentList';
import { umSinceBeginning } from '../pages-equipment/equipmentFieldUtils';
import { UnitCostPanel } from './SingleUnitCostTab';
import type { ExpenseFilterCriteria } from '../../types/costTrackingTypes';
import '../../css/CostTrackingDashboard.css';
import { BackButton, Btn, BtnIconOnly, Card, IconPlus, IconSearch, InputField, PageHeader } from '../ui';

type CostTrackingView =
    | { type: 'list' }
    | { type: 'add-expense' }
    | { type: 'edit-expense'; expenseId: string }
    | { type: 'view-detail'; expenseId: string };

/**
 * Main container for the Cost Tracking module. Rendered when page === "cost tracking" in Dashboard.
 * - Displays header with title, action buttons, and search icon
 * - Manages internal navigation state for sub-views: list (default), add-expense, edit-expense, view-detail
 * - Wraps content in CostTrackingContext provider
 *
 * Validates: Requirements 2.1, 1.1
 */
export const CostTrackingDashboardContent = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { companyUnits } = useEquipmentContext();
    const [view, setView] = useState<CostTrackingView>({ type: 'list' });
    const [, setSelectedExpenseId] = useState<string | null>(null);
    const [activeCategory, setActiveCategory] = useState<string | null>(null);
    const [filterCriteria, setFilterCriteria] = useState<ExpenseFilterCriteria>({});
    const [searchQuery, setSearchQuery] = useState('');
    const [searchOpen, setSearchOpen] = useState(false);
    const expenseId = searchParams.get('expense');
    const unitId = searchParams.get('unit');
    const showEquipment = searchParams.get('view') === 'equipment';
    const selectedUnit = unitId ? companyUnits.find((unit) => unit.id === unitId) ?? null : null;

    const handleAddExpense = () => {
        setSearchParams({});
        setView({ type: 'add-expense' });
    };

    const handleEditExpense = (expenseId: string) => {
        setSelectedExpenseId(expenseId);
        setView({ type: 'edit-expense', expenseId });
    };

    const handleViewDetail = (expenseId: string) => {
        setSelectedExpenseId(expenseId);
        setView({ type: 'view-detail', expenseId });
    };

    const handleBackToList = () => {
        setSelectedExpenseId(null);
        setView({ type: 'list' });
        setSearchParams({});
    };

    const handleOpenEquipment = () => {
        setView({ type: 'list' });
        setSearchParams({ view: 'equipment' });
    };

    const handleOpenUnit = (nextUnitId: string) => {
        setSearchParams({ unit: nextUnitId });
    };

    const handleViewUnitExpense = (nextExpenseId: string) => {
        const next: Record<string, string> = { expense: nextExpenseId };
        if (unitId) next.unit = unitId;
        setSearchParams(next);
    };

    const handleBackFromExpense = () => {
        if (unitId) {
            setSearchParams({ unit: unitId });
            return;
        }
        setSearchParams({});
    };

    const handleReports = () => {
        navigate('/reports');
    };

    // Render sub-views when navigating to add/edit/view-detail
    const renderSubView = () => {
        switch (view.type) {
            case 'add-expense':
                return <RecordExpenseForm onBack={handleBackToList} onSave={handleBackToList} />;
            case 'edit-expense':
                return (
                    <RecordExpenseForm expenseId={view.expenseId} onBack={handleBackToList} onSave={handleBackToList} />
                );
            case 'view-detail':
                return <ExpenseRecordDetail expenseId={view.expenseId} onBack={handleBackToList} />;
            default:
                return null;
        }
    };

    const renderEquipmentViews = () => {
        if (expenseId) {
            return (
                <ExpenseRecordDetail
                    expenseId={expenseId}
                    onBack={handleBackFromExpense}
                    backLabel={unitId ? 'Equipment' : 'All costs'}
                />
            );
        }
        if (unitId) {
            if (!selectedUnit) {
                return (
                    <Card variant="card-lg" className="ct-list-card">
                        <BackButton onClick={handleOpenEquipment} buttonText="Equipment" />
                        <p className="ct-unit-empty-msg">This unit is not available.</p>
                    </Card>
                );
            }
            return (
                <div className="ct-unit-tab">
                    <BackButton onClick={handleOpenEquipment} buttonText="Equipment" />
                    <h2 className="ct-unit-detail-title">{selectedUnit.name}</h2>
                    <UnitCostPanel
                        unitId={selectedUnit.id}
                        currentUMCounterValue={umSinceBeginning(selectedUnit)}
                        unitOfMeasure={selectedUnit.unitOfMeasure ?? 'UM'}
                        onViewExpense={handleViewUnitExpense}
                        showModuleLink={false}
                    />
                </div>
            );
        }
        if (showEquipment) {
            return (
                <div className="ct-equipment-view">
                    <BackButton onClick={handleBackToList} buttonText="All costs" />
                    <CostTrackingEquipmentList onOpenUnit={handleOpenUnit} />
                </div>
            );
        }
        return null;
    };

    const renderListView = () => (
        <Card variant="card-lg" className="ct-list-card">
            <ExpenseFilterBar
                activeCategory={activeCategory}
                onCategoryChange={setActiveCategory}
                onFilterChange={setFilterCriteria}
            />
            <ExpenseTable
                activeCategory={activeCategory}
                filterCriteria={filterCriteria}
                searchQuery={searchQuery}
                onViewExpense={handleViewDetail}
                onEditExpense={handleEditExpense}
            />
            <CostSummaryFooter />
        </Card>
    );

    return (
        <PMProvider>
            <CostTrackingProvider>
                <div className="page-container ct-dashboard-container">
                    {/* Header */}
                    <PageHeader
                        title="Cost Tracking"
                        className="no-print"
                        actions={
                            <div className="ct-header-actions">
                                <Btn
                                    variant="primary"
                                    buttonShape="pill"
                                    buttonIcon={<IconPlus />}
                                    buttonText={
                                        <>
                                            <span className="ct-label-full">Add Expense/Cost</span>
                                            <span className="ct-label-short">Add Cost</span>
                                        </>
                                    }
                                    buttonOnClick={handleAddExpense}
                                />
                                <Btn
                                    variant="secondary"
                                    buttonShape="pill"
                                    buttonText={
                                        <>
                                            <span className="ct-label-full">Equipment</span>
                                            <span className="ct-label-short">Equipment</span>
                                        </>
                                    }
                                    buttonOnClick={handleOpenEquipment}
                                />
                                <Btn
                                    variant="secondary"
                                    buttonShape="pill"
                                    buttonText={
                                        <>
                                            <span className="ct-label-full">Expense/Cost Reports</span>
                                            <span className="ct-label-short">Cost Reports</span>
                                        </>
                                    }
                                    buttonOnClick={handleReports}
                                />
                                <div className="ct-header-search">
                                    {searchOpen && (
                                        <InputField
                                            className="ct-search-input"
                                            type="search"
                                            placeholder="Search expenses..."
                                            value={searchQuery}
                                            onChange={setSearchQuery}
                                            ariaLabel="Search expense records"
                                            autoFocus
                                        />
                                    )}
                                    <BtnIconOnly
                                        size="sm"
                                        icon={<IconSearch />}
                                        ariaLabel={searchOpen ? 'Hide search' : 'Search expenses'}
                                        buttonClasses="ct-btn-search-icon"
                                        buttonAriaProps={{ 'aria-expanded': searchOpen }}
                                        onClick={() => {
                                            setSearchOpen((open) => {
                                                if (open) setSearchQuery('');
                                                return !open;
                                            });
                                        }}
                                    />
                                </div>
                            </div>
                        }
                    />

                    {/* Content */}
                    {expenseId || unitId || showEquipment
                        ? renderEquipmentViews()
                        : view.type === 'list'
                          ? renderListView()
                          : renderSubView()}
                </div>
            </CostTrackingProvider>
        </PMProvider>
    );
};
